import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Puts a new page at the top of itself.
 *
 * A browser resets scroll on a document load; a client-side router does not, so
 * following a footer link from halfway down the Terms landed halfway down the
 * Disclosures — a page apparently opened in the middle, with its contents rail
 * highlighting whichever section happened to be in view.
 *
 * Keyed on pathname alone. A hash change is someone jumping to a section within
 * the page they are already reading, and resetting scroll there would fight the
 * anchor rather than help it. When a URL arrives carrying a hash, the target is
 * scrolled to instead — the browser cannot do it for us, because the element
 * does not exist until the route has rendered.
 *
 * React Router's own ScrollRestoration is not an option here: it needs a data
 * router, and this app mounts BrowserRouter with a plain Routes tree.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // The anchor is only in the DOM once the new route has painted.
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }

    // "instant" rather than the default: globals.css sets scroll-behavior on the
    // document, and a smooth scroll through a long legal page on every route
    // change reads as the page sliding around rather than as a new page.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
}
